// Component to contain the SideBar content -- either Actions, Details, History, or Settings
import React from 'react';

import { styled } from '@mui/material/styles';
import { theme } from '../../theme/theme';
import SideBarActions from './SideBarActions';
import SideBarDetails from './SideBarDetails';
import SideBarHistory from './SideBarHistory';
import SideBarSettings from './SideBarSettings';
import SideBarContentDetails from './SideBarContentDetails';
import SideBarUncommissioned from './SideBarUncommissioned';
import { getIconStyling } from './CloudSystemIconHelpers';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import {
  Box,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import TabPanel, { a11yTabProps } from '../../components/ui/TabPanel';

import { useGetCloudSystemQuery } from "./apiCloudSystem";

const NoMaxWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
});

// JS doesn't have interfaces
//  use this commented out definition as a reference for the props that should be accepted for this component 
// export interface CloudDetailsSideBarContentProps {
//   cloudSystemSerial: string
//   onNeedsGet: function
// }

const DETAILS_TAB_INDEX = 0;
const ACTIONS_TAB_INDEX = 1;
const CONTENT_TAB_INDEX = 2;
const HISTORY_TAB_INDEX = 3;
const SETTINGS_TAB_INDEX = 4;

const CloudDetailsSideBarContent = (props) => {
  // State
  const [tabIndex, setTabIndex] = React.useState(0);
  const [getTrigger, setGetTrigger] = React.useState(1); // Changing part of the object passed to the Redux hook triggers the query for subsequent calls

  // Disabled check (If get-settings or change-settings Action is pending, Settings' inputs are disabled)
  const [gettingSettings, setGettingSettings] = React.useState(false);
  const [changingSettings, setChangingSettings] = React.useState(false);
  const [waitingOnSettings, setWaitingOnSettings] = React.useState(false);
  const [currentSettingsInputs,setCurrentSettingsInputs] = React.useState({})

  const handleTabChanged = (event, value) => {
    setTabIndex(value);
    setCurrentSettingsInputs({});
  };

  const onNeedsGet = (forwardOnly) => {
    if (!forwardOnly) {
      setGetTrigger(g => (g === 1 ? 2 : 1)); // Changing the data will trigger GET call
    }
    
    // Also tell parent to Get summaries
    if (props.onNeedsGet) { 
      props.onNeedsGet();
    }
  };

  const {
    data: cloudSystemData,
    isLoading: isGetLoading,
    isSuccess: isGetSuccess,
    isError: isGetError,
    error: getError,
  } = useGetCloudSystemQuery({"serialNumber": props.cloudSystemSerial, trigger: getTrigger}, { pollingInterval: 5000, refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true });

  React.useEffect(() => {
    var gettingSettings = cloudSystemData?.CloudSystem?.actions?.find(a => a.method === 'get-settings' && ['Unsent', 'WaitingForAck', 'Acknowledged'].includes(a.status)) !== undefined;
    var changingSettings = cloudSystemData?.CloudSystem?.actions?.find(a => a.method === 'change-settings' && ['Unsent', 'WaitingForAck', 'Acknowledged'].includes(a.status)) !== undefined;
    var isActive = gettingSettings || changingSettings;

    //If we were waiting on settings, and we just got them back, we don't know need to keep the users inputs because we have real data now.
    if(!isActive && waitingOnSettings)
    {
      setCurrentSettingsInputs({})
    }

    setGettingSettings(gettingSettings);
    setChangingSettings(changingSettings);
    setWaitingOnSettings(isActive);
  }, [cloudSystemData?.CloudSystem?.actions]);

  const iconStyling = getIconStyling(cloudSystemData?.CloudSystem?.activeStatus, props.cloudSystemSerial);

  const commissionedElements = (
    <>
      <Grid container item sx={{justifyContent: 'center', my: 2}}>
        <Stack>
          <Typography
            fontSize={26}
            fontWeight={700}
            color={theme.palette.secondary.contrastText}
            sx={{mx: 'auto'}}
          >
            {cloudSystemData?.CloudSystem?.shortName}
          </Typography>
          <Typography
            fontSize={18}
            fontWeight={400}
            color={'gray'}
            sx={{mx: 'auto'}}
          >
            {cloudSystemData?.CloudSystem?.customer}
          </Typography>
        </Stack>
      </Grid>

      <Grid container direction='row' alignItems='center' justifyContent='space-evenly' sx={{my: 2}}>
        <Grid item xs={3} sx={{ color: theme.palette.primary.contrastText }}>
          <Box height={76} display="flex" alignItems="center" justifyContent="center">
            <Stack display={'flex'} alignItems="center" justifyContent={'center'}>
              <NoMaxWidthTooltip title={<Typography fontSize={16}>{iconStyling.networkTooltip}</Typography>}>
                {iconStyling.networkIcon}
              </NoMaxWidthTooltip>
              <Typography variant='h6' color={iconStyling.textColor}>
                Network
              </Typography>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={3} sx={{ color: theme.palette.primary.contrastText }}>
          <Box height={76} display="flex" alignItems="center" justifyContent="center">
            <Stack display={'flex'} alignItems="center" justifyContent={'center'}>
              <NoMaxWidthTooltip title={<Typography fontSize={16}>{iconStyling.aircraftTooltip}</Typography>}>
                <Stack direction="row" display="flex" alignItems={"center"} justifyContent={'center'}>
                  {iconStyling.aircraftIcon}
                  <Typography variant="h5" color={iconStyling.aircraftStatusColor}>
                    {cloudSystemData?.CloudSystem?.activeStatus?.uniqueACIDSWithinHealthyRange ?? 0}
                  </Typography>
                </Stack>
              </NoMaxWidthTooltip>
              <Typography variant='h6' color={iconStyling.textColor}>
                Aircraft
              </Typography>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={3} sx={{ color: theme.palette.primary.contrastText }}>
          <Box height={76} display="flex" alignItems="center" justifyContent="center">
            <Stack display={'flex'} alignItems="center" justifyContent={'center'}>
              <NoMaxWidthTooltip title={<Typography fontSize={16}>{iconStyling.softwareTooltip}</Typography>}>
                {iconStyling.softwareIcon}
              </NoMaxWidthTooltip>
              <Typography variant='h6' color={iconStyling.textColor}>
                Software
              </Typography>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={3} sx={{ color: theme.palette.primary.contrastText }}>
          <Box height={76} display="flex" alignItems="center" justifyContent="center">
            <Stack display={'flex'} alignItems="center" justifyContent={'center'}>
              <NoMaxWidthTooltip title={<Typography fontSize={16}>{iconStyling.contentTooltip}</Typography>}>
                {iconStyling.contentIcon}
              </NoMaxWidthTooltip>
              <Typography variant='h6' color={iconStyling.textColor}>
                Content
              </Typography>
            </Stack>
          </Box>
        </Grid>
      </Grid>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: theme.palette.secondary.main }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChanged}
          aria-label="cloud system details sidebar tabs"
          textColor="inherit"
          TabIndicatorProps={{style: {background:'#FFFFFF'}}}
          centered
        >
          <Tab label="Details" sx={{ color: theme.palette.secondary.contrastText }} {...a11yTabProps(0)} />
          <Tab label="Actions" sx={{ color: theme.palette.secondary.contrastText,  }} {...a11yTabProps(1)} />
          <Tab label="Content" sx={{ color: theme.palette.secondary.contrastText,  }} {...a11yTabProps(2)} />
          <Tab label="History" sx={{ color: theme.palette.secondary.contrastText }} {...a11yTabProps(3)} />
          <Tab label="Settings" sx={{ color: theme.palette.secondary.contrastText }} {...a11yTabProps(4)} />
        </Tabs>
      </Box>

      {waitingOnSettings && tabIndex === SETTINGS_TAB_INDEX && (
      <Box width={"100%"}>
        <Typography align='center' backgroundColor={theme.palette.tertiary.main} color={theme.palette.tertiary.contrastText}>
          {gettingSettings ? "Retrieving Settings..." : changingSettings ? "Applying Settings..." : "..."}
        </Typography>
      </Box>)}

      {/* Details Tab */}
      <TabPanel value={tabIndex} index={DETAILS_TAB_INDEX}>
        <SideBarDetails
          cloudSystemDetails={cloudSystemData?.CloudSystem}
          onNeedsGet={onNeedsGet}
        />
      </TabPanel>

      {/* Actions Tab */} 
      <TabPanel value={tabIndex} index={ACTIONS_TAB_INDEX}>
        <SideBarActions
          cloudSystemDetails={cloudSystemData?.CloudSystem}
          onNeedsGet={onNeedsGet}
          onClose={props.onClose}
        />
      </TabPanel>

      {/* Content Tab */}
      <TabPanel value={tabIndex} index={CONTENT_TAB_INDEX}>
        <SideBarContentDetails
          cloudSystemDetails={cloudSystemData?.CloudSystem}
        />
      </TabPanel>

      {/* History Tab */}
      <TabPanel value={tabIndex} index={HISTORY_TAB_INDEX} paddingBottom={0}>
        <SideBarHistory
          cloudSystemDetails={cloudSystemData?.CloudSystem}
          onNeedsGet={onNeedsGet}
        />
      </TabPanel>

      {/* Settings Tab */}
      <TabPanel value={tabIndex} index={SETTINGS_TAB_INDEX}>
        <SideBarSettings
          cloudSystemDetails={cloudSystemData?.CloudSystem}
          waitingOnSettings={waitingOnSettings}
          currentSettingsInputs={currentSettingsInputs}
          setCurrentSettingsInputs={setCurrentSettingsInputs}
          onNeedsGet={onNeedsGet}
        />
      </TabPanel>
    </>
  );

  const uncommissionedElements = (
    <>
      <SideBarUncommissioned 
        cloudSystemDetails={cloudSystemData?.CloudSystem}
        onNeedsGet={onNeedsGet}
        onClose={props.onClose}
      />
    </>
  );

  return (
    <>
      {isGetLoading ? 
      (<>
        Loading...
      </>) : 
      isGetSuccess ?
      (<>
        {cloudSystemData?.CloudSystem?.isCommissioned && commissionedElements}
        {!cloudSystemData?.CloudSystem?.isCommissioned && uncommissionedElements}
      </>) : 
      isGetError ?
      (<>
        Error loading: {getError?.data?.error}
      </>) :
      (<>
        Not sure what happened
      </>)}
    </>
  );
};

export default CloudDetailsSideBarContent;